<template>
  <div class="modal-overlay" v-if="visible" @click.self="close">
    <div class="modal-container">
      <div class="modal-header">
        <h3>{{ isCustomUpload ? '上传自定义依赖' : '添加在线依赖库' }}</h3>
        <button class="close-btn" @click="close">×</button>
      </div>

      <div class="modal-body">
        <!-- 在线依赖库 -->
        <div v-if="!isCustomUpload" class="online-dependency">
          <div class="form-group">
            <label>依赖包名称</label>
            <input 
              type="text" 
              v-model="form.name"
              placeholder="例如: lodash, axios, dayjs"
              class="form-input"
            >
          </div>

          <div class="form-group">
            <label>版本号</label>
            <input 
              type="text" 
              v-model="form.version"
              placeholder="例如: 4.17.21"
              class="form-input"
            >
          </div>

          <div class="form-group">
            <label>模块类型</label>
            <div class="radio-group">
              <label class="radio-item">
                <input type="radio" value="umd" v-model="form.type"> UMD
              </label>
              <label class="radio-item">
                <input type="radio" value="es" v-model="form.type"> ES Module
              </label>
              <label class="radio-item">
                <input type="radio" value="cjs" v-model="form.type"> CommonJS
              </label>
            </div>
          </div>

          <div class="form-group">
            <label>CDN源选择</label>
            <select v-model="selectedCdn" class="form-select">
              <option value="jsdelivr">jsDelivr (推荐)</option>
              <option value="unpkg">UNPKG</option>
              <option value="cdnjs">cdnjs</option>
              <option value="custom">自定义URL</option>
            </select>
          </div>

          <div class="form-group" v-if="selectedCdn === 'custom'">
            <label>自定义URL</label>
            <input 
              type="url" 
              v-model="form.url"
              placeholder="https://example.com/library.js"
              class="form-input"
            >
          </div>

          <div class="form-group" v-else>
            <label>生成的URL</label>
            <input 
              type="text" 
              :value="generatedUrl"
              readonly
              class="form-input readonly"
            >
          </div>

          <div class="form-group" v-if="form.type === 'umd'">
            <label>全局变量名</label>
            <input 
              type="text" 
              v-model="form.globalName"
              placeholder="例如: _, axios, dayjs"
              class="form-input"
            >
            <small class="form-hint">UMD模块通常会在window对象上暴露一个全局变量</small>
          </div>

          <div class="form-group">
            <label>描述（可选）</label>
            <textarea 
              v-model="form.description"
              placeholder="简要描述这个依赖的用途"
              class="form-textarea"
            ></textarea>
          </div>
        </div>

        <!-- 自定义上传 -->
        <div v-else class="custom-upload">
          <div class="upload-area" @drop="onDrop" @dragover.prevent @dragenter.prevent>
            <div class="upload-content">
              <span class="upload-icon"><ElIcon><FolderOpened /></ElIcon></span>
              <p>拖拽文件到此处或点击选择文件</p>
              <p class="upload-hint">支持 .js, .mjs, .cjs 文件</p>
              <input 
                type="file" 
                ref="fileInput"
                @change="onFileSelect"
                accept=".js,.mjs,.cjs"
                class="file-input"
              >
            </div>
          </div>

          <div v-if="uploadedFile" class="uploaded-file">
            <div class="file-info">
              <span class="file-name">{{ uploadedFile.name }}</span>
              <span class="file-size">{{ formatFileSize(uploadedFile.size) }}</span>
            </div>
            <button class="remove-file" @click="removeFile">×</button>
          </div>

          <div class="form-group">
            <label>依赖名称</label>
            <input 
              type="text" 
              v-model="form.name"
              placeholder="为这个依赖起个名字"
              class="form-input"
            >
          </div>

          <div class="form-group">
            <label>版本号</label>
            <input 
              type="text" 
              v-model="form.version"
              placeholder="1.0.0"
              class="form-input"
            >
          </div>

          <div class="form-group">
            <label>模块类型</label>
            <div class="radio-group">
              <label class="radio-item">
                <input type="radio" value="umd" v-model="form.type"> UMD
              </label>
              <label class="radio-item">
                <input type="radio" value="es" v-model="form.type"> ES Module
              </label>
              <label class="radio-item">
                <input type="radio" value="cjs" v-model="form.type"> CommonJS
              </label>
            </div>
          </div>

          <div class="form-group" v-if="form.type === 'umd'">
            <label>全局变量名</label>
            <input 
              type="text" 
              v-model="form.globalName"
              placeholder="例如: MyLibrary"
              class="form-input"
            >
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-secondary" @click="close">取消</button>
        <button 
          class="btn btn-primary" 
          @click="submit"
          :disabled="!canSubmit"
        >
          {{ isCustomUpload ? '上传' : '添加' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch } from 'vue'
import { addDependency, type Dependency } from 'mflows'
// Element Plus 组件与图标从本地插件导出按需导入
import { ElIcon, FolderOpened } from '@/plugins/elementPlus'
import { ElMessage } from '@/plugins/elementPlus'

const props = defineProps<{
  visible: boolean
  isCustomUpload?: boolean
}>()

const emit = defineEmits<{
  close: []
  success: [dependency: Dependency]
}>()

// 表单数据
const form = reactive({
  name: '',
  version: '',
  type: 'umd' as 'umd' | 'cjs' | 'es',
  url: '',
  globalName: '',
  description: ''
})

// CDN 选择
const selectedCdn = ref('jsdelivr')
const uploadedFile = ref<File | null>(null)
const fileInput = ref<HTMLInputElement>()

// CDN URL 生成
const generatedUrl = computed(() => {
  if (!form.name || !form.version || selectedCdn.value === 'custom') return ''
  
  const name = form.name
  const version = form.version
  
  switch (selectedCdn.value) {
    case 'jsdelivr':
      return `https://cdn.jsdelivr.net/npm/${name}@${version}/dist/${name}.min.js`
    case 'unpkg':
      return `https://unpkg.com/${name}@${version}/dist/${name}.min.js`
    case 'cdnjs':
      return `https://cdnjs.cloudflare.com/ajax/libs/${name}/${version}/${name}.min.js`
    default:
      return ''
  }
})

// 提交验证
const canSubmit = computed(() => {
  if (!form.name || !form.version || !form.type) return false
  
  if (props.isCustomUpload) {
    return !!uploadedFile.value
  } else {
    return selectedCdn.value === 'custom' ? !!form.url : !!generatedUrl.value
  }
})

// 监听模块类型变化，自动填充全局变量名
watch(() => form.name, (newName) => {
  if (form.type === 'umd' && newName && !form.globalName) {
    // 尝试猜测全局变量名
    const commonGlobals: Record<string, string> = {
      'lodash': '_',
      'axios': 'axios',
      'dayjs': 'dayjs',
      'moment': 'moment',
      'jquery': '$'
    }
    form.globalName = commonGlobals[newName.toLowerCase()] || newName
  }
})

// 文件拖拽处理
function onDrop(event: DragEvent) {
  event.preventDefault()
  const files = event.dataTransfer?.files
  if (files && files.length > 0) {
    handleFile(files[0])
  }
}

// 文件选择处理
function onFileSelect(event: Event) {
  const target = event.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    handleFile(target.files[0])
  }
}

// 处理文件
function handleFile(file: File) {
  const validExtensions = ['.js', '.mjs', '.cjs']
  const fileExtension = file.name.substring(file.name.lastIndexOf('.'))
  
  if (!validExtensions.includes(fileExtension)) {
    ElMessage.warning('只支持 .js, .mjs, .cjs 文件')
    return
  }
  
  uploadedFile.value = file
  
  // 根据文件扩展名推测模块类型
  if (fileExtension === '.mjs') {
    form.type = 'es'
  } else if (fileExtension === '.cjs') {
    form.type = 'cjs'
  }
  
  // 从文件名推测依赖名称
  if (!form.name) {
    form.name = file.name.replace(/\.(js|mjs|cjs)$/, '')
  }
}

// 移除文件
function removeFile() {
  uploadedFile.value = null
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

// 格式化文件大小
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// 上传文件到服务器 (模拟)
async function uploadFile(file: File): Promise<string> {
  // 在实际应用中，这里应该上传到服务器
  // 现在我们使用 FileReader 转换为 data URL
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      // 生成一个临时的 blob URL
      const blob = new Blob([result], { type: 'application/javascript' })
      const url = URL.createObjectURL(blob)
      resolve(url)
    }
    reader.onerror = () => reject(new Error('文件读取失败'))
    reader.readAsText(file)
  })
}

// 提交表单
async function submit() {
  try {
    let url = ''
    
    if (props.isCustomUpload && uploadedFile.value) {
      // 上传自定义文件
      url = await uploadFile(uploadedFile.value)
    } else {
      // 使用在线URL
      url = selectedCdn.value === 'custom' ? form.url : generatedUrl.value
    }
    
    const dependency = {
      name: form.name,
      version: form.version,
      type: form.type,
      url: url,
      source: props.isCustomUpload ? 'custom' : 'cdn',
      description: form.description,
      globals: form.type === 'umd' && form.globalName 
        ? { [form.globalName]: form.name }
        : undefined
    } as Omit<Dependency, 'id'>
    
    addDependency(dependency)
    emit('success', { ...dependency, id: '' })
    close()
  } catch (error) {
    console.error('Failed to add dependency:', error)
    ElMessage.error('添加依赖失败，请检查配置')
  }
}

// 关闭模态框
function close() {
  // 重置表单
  Object.assign(form, {
    name: '',
    version: '',
    type: 'umd',
    url: '',
    globalName: '',
    description: ''
  })
  selectedCdn.value = 'jsdelivr'
  uploadedFile.value = null
  if (fileInput.value) {
    fileInput.value.value = ''
  }
  
  emit('close')
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background: #2d2d2d;
  border-radius: 8px;
  width: 500px;
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  color: #cccccc;
  font-size: 12px; /* base font size for normal text */
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px; /* 20px 24px -> 16px 16px */
  border-bottom: 1px solid #404040;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  color: #999;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  
  &:hover {
    background: #404040;
    color: #cccccc;
  }
}

.modal-body {
  padding: 16px; /* 24px -> 16px */
  max-height: 60vh;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 12px; /* 20px -> 12px */
}

.form-group label {
  display: block;
  margin-bottom: 6px; /* 8px -> 6px */
  font-size: 12px; /* 14px -> 12px */
  font-weight: 500;
  color: #cccccc;
}

.form-input, .form-select, .form-textarea {
  width: 100%;
  padding: 6px 10px; /* 8px 12px -> 6px 10px */
  background: #333;
  border: 1px solid #555;
  border-radius: 4px;
  color: #cccccc;
  font-size: 12px; /* 14px -> 12px */
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #007acc;
  }
  
  &.readonly {
    background: #2a2a2a;
    color: #999;
  }
  
  &::placeholder {
    color: #666;
  }
}

.form-textarea {
  min-height: 64px; /* 80px -> 64px */
  resize: vertical;
}

.form-hint {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #999;
}

.radio-group {
  display: flex;
  gap: 12px; /* 16px -> 12px */
}

.radio-item {
  display: flex;
  align-items: center;
  gap: 4px; /* 6px -> 4px */
  cursor: pointer;
  
  input[type="radio"] {
    margin: 0;
  }
}

.upload-area {
  border: 2px dashed #555;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;
  
  &:hover {
    border-color: #007acc;
  }
}

.upload-content {
  position: relative;
}

.upload-icon {
  font-size: 48px;
  display: block;
  margin-bottom: 16px;
}

.upload-area p {
  margin: 8px 0;
  color: #cccccc;
}

.upload-hint {
  font-size: 12px;
  color: #999;
}

.file-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.uploaded-file {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #333;
  border-radius: 4px;
  margin-top: 16px;
}

.file-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.file-name {
  font-weight: 500;
  color: #cccccc;
}

.file-size {
  font-size: 12px;
  color: #999;
}

.remove-file {
  background: none;
  border: none;
  color: #f85149;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  
  &:hover {
    background: rgba(248, 81, 73, 0.1);
  }
}

.modal-footer {
  display: flex;
  gap: 8px; /* 12px -> 8px */
  justify-content: flex-end;
  padding: 16px; /* 20px 24px -> 16px */
  border-top: 1px solid #404040;
}

.btn {
  padding: 6px 12px; /* 8px 16px -> 6px 12px */
  border: none;
  border-radius: 4px;
  font-size: 12px; /* 14px -> 12px */
  cursor: pointer;
  transition: background 0.2s;
  
  &.btn-secondary {
    background: #555;
    color: #cccccc;
    
    &:hover {
      background: #666;
    }
  }
  
  &.btn-primary {
    background: #007acc;
    color: white;
    
    &:hover {
      background: #005a9e;
    }
    
    &:disabled {
      background: #444;
      color: #666;
      cursor: not-allowed;
    }
  }
}
</style>